import type { TrayTicketPatient, TrayTicketItem } from '../../../types/trayTicket';
import { useTrayTicketColorConfig } from '../../../api/trayTicketApi';
import { argbToHex } from '../../../types/facility';
import { useScreenSummary } from '../../../components/common/ScreenSummary';
import type { ScreenSummaryValue, ScreenSummarySection } from '../../../components/common/ScreenSummary';
import { useGlobalStore } from '../../../stores/globalStore';
import './TrayTicketVisualizerContent.css';

// Special glyphs used by the WinForms tray-ticket text
// composer (`Characters.CommonCharacters.vb`). Kept as
// constants so the format-line function reads cleanly.
const GLYPH_BLACK_SQUARE = '■'; // ■  U+25A0 — position-3/4 prefix
const GLYPH_ARROW = '→';        // →  U+2192 — separates itemType from serviceItem
const GLYPH_BLACK_CIRCLE = '●'; // ●  U+25CF — overridden-item "OK" suffix
const GLYPH_INCREMENT = '∆';    // ∆  U+2206 — temp-diet-modified prefix

/** Sentinel PKey_Prd_Portion for the "No portion" row —
 *  triggers portion-first ordering per WinForms
 *  `EnumerationsConstantsCollections.vb:13`. */
const PKEY_PRD_PORTION_NO = 15;

interface TrayTicketVisualizerContentProps {
  patient: TrayTicketPatient;
}

// DevExpress `BorderSide` bitmask — matches WinForms
// `RenderMenu` .vb:2540-2616 which tests each flag independently
// and draws its own line. Each item may have any combination.
const BORDER_LEFT = 1;
const BORDER_TOP = 2;
const BORDER_RIGHT = 4;
const BORDER_BOTTOM = 8;

/** Convert ARGB int to CSS hex color */
function boxColorToHex(argb: number | null): string | undefined {
  if (!argb) return undefined;
  return argbToHex(argb);
}

/**
 * Per-item border styling from the DevExpress `BorderSide` bitmask.
 * Each side (Left / Top / Right / Bottom) is checked independently
 * — matches WinForms `RenderMenu` (.vb:2540-2616) which walks each
 * flag and calls `DrawLine` separately. Color from `Argb_BoxColor`,
 * falling back to black (WinForms default at .vb:2549 / 2560 / 2594
 * / 2614). Consecutive items whose side borders align form a visual
 * box naturally.
 */
function borderStyle(item: TrayTicketItem): React.CSSProperties {
  if (!item.borderSide || item.borderSide === 0) return {};
  const color = boxColorToHex(item.boxColor) || '#000';
  const style: React.CSSProperties = {};
  if (item.borderSide & BORDER_TOP) style.borderTop = `1px solid ${color}`;
  if (item.borderSide & BORDER_BOTTOM) style.borderBottom = `1px solid ${color}`;
  if (item.borderSide & BORDER_LEFT) style.borderLeft = `1px solid ${color}`;
  if (item.borderSide & BORDER_RIGHT) style.borderRight = `1px solid ${color}`;
  return style;
}

/** Render a single item row */
/** Position-based prefix — mirrors WinForms `ItemPrefix`
 *  (frmDietech_Prd_TrayTicketVisualizer.vb:3232-3243):
 *    position < 3 → ""
 *    position = 3 → "■ "
 *    position = 4 → "■ ■ "
 */
function itemPrefix(position: number | null | undefined): string {
  if (position == null || position < 3) return '';
  if (position === 3) return `${GLYPH_BLACK_SQUARE} `;
  return `${GLYPH_BLACK_SQUARE} ${GLYPH_BLACK_SQUARE} `;
}

/** WinForms `FormatItemText` — trailing space if value present. */
function formatText(value: string | null | undefined, addSpace = true): string {
  const v = (value ?? '').trim();
  if (v === '') return '';
  return addSpace ? `${v} ` : v;
}

/** WinForms `FormatItemTextWithArrow` — appends `itemType` after
 *  `itmDescr`, then a `→ ` when a service item follows. */
function formatItemWithArrow(itmDescr: string | null | undefined, itemType: string | null | undefined, hasServiceItem: boolean): string {
  const descr = (itmDescr ?? '').trim();
  if (descr === '') return '';
  const type = (itemType ?? '').trim();
  const typeSuffix = type === '' ? '' : ` ${type}`;
  return hasServiceItem ? `${descr}${typeSuffix} ${GLYPH_ARROW} ` : `${descr}${typeSuffix}`;
}

function ItemRow({
  item, colorConfig, useCaps,
}: {
  item: TrayTicketItem;
  colorConfig: ReturnType<typeof useTrayTicketColorConfig>;
  useCaps: boolean;
}) {
  if (item.isCategoryHeader) {
    // A drawn rule. It has no content to speak, and as a non-listitem
    // child of the item list it would otherwise be announced as a stray
    // group.
    return <div className="ttvc-category-separator" aria-hidden="true" />;
  }

  // Mealtime / diet-order notes render bold — matches WinForms
  // `IsMealtimeNote OrElse IsDietOrderNote` branch at .vb:2430,2450.
  const isBoldNote = item.isMealtimeNote || item.isDietOrderNote;

  // Item notes — plain text, no prefix / consist / portion / arrow
  // formatting (matches WinForms `IsItemNote` branch at .vb:2471).
  // Note rows STILL honor `borderSide` — WinForms `RenderMenu`
  // draws box borders on every row inside a box regardless of
  // note vs item type (.vb:2540-2616). Without this, a note
  // sitting inside a boxed category leaves a visible gap in the
  // box's left/right borders.
  if (item.isNote) {
    const text = useCaps ? (item.itemName ?? '').toUpperCase() : (item.itemName ?? '');
    // Bold notes (mealtime / diet-order — e.g. "Diet Δ B") use the
    // WinForms `fntNote`: bigger, bold, NOT italic, and BLACK not
    // orange. Also NOT indented — they render flush-left like a
    // header. See frmDietech_Prd_TrayTicketVisualizer.vb:2254-2262.
    // Plain item notes keep the italic-orange indented styling.
    if (isBoldNote) {
      return (
        <div
          role="listitem"
          className="ttvc-item"
          style={{
            fontWeight: 700,
            fontStyle: 'normal',
            color: item.itemColor && colorConfig?.useColor
              ? argbToHex(item.itemColor) : '#000',
            paddingLeft: 4,
            ...borderStyle(item),
          }}
        >
          {text}
        </div>
      );
    }
    return (
      <div role="listitem" className="ttvc-item ttvc-item-note" style={borderStyle(item)}>
        {text}
      </div>
    );
  }

  // Item color — TT_ItemColor Argb from server.
  const itemColorStyle: React.CSSProperties = {};
  if (colorConfig?.useColor && item.itemColor) {
    itemColorStyle.color = argbToHex(item.itemColor);
  }
  if (isBoldNote) itemColorStyle.fontWeight = 700;

  // Compose the full line per WinForms `RenderMenu` non-note branch
  // (.vb:2474-2492). Order:
  //   • Default: prefix + consist + portion + itmDescr [itemType → serviceItem]
  //   • FKey_Prd_Portion == PKEY_PRD_PORTION_NO: portion-first
  //     (portion + consist ...).
  //   • Overridden item → append "  ● OK".
  //   • Position=2 + MealPatternModifiedByTempDiet → prepend "∆ ".
  const prefix = itemPrefix(item.position);
  const hasService = ((item.serviceItem ?? '').trim().length > 0);
  const portionFirst = item.fKey_Prd_Portion === PKEY_PRD_PORTION_NO;
  const dietSlots = portionFirst
    ? formatText(item.portion) + formatText(item.consistency)
    : formatText(item.consistency) + formatText(item.portion);
  let body = ''
    + prefix
    + dietSlots
    + formatItemWithArrow(item.itemName, item.itemType, hasService)
    + formatText(item.serviceItem, false);
  if (item.overriddenItem) body += `  ${GLYPH_BLACK_CIRCLE}  OK`;
  if (item.position === 2 && item.mealPatternModifiedByTempDiet) {
    body = `${GLYPH_INCREMENT} ${body}`;
  }
  const line = useCaps ? body.toUpperCase() : body;

  return (
    <div
      role="listitem"
      className={`ttvc-item ${item.isSubstitution ? 'ttvc-item-sub' : ''} ${item.isAlwaysServed ? 'ttvc-item-always' : ''}`}
      style={borderStyle(item)}
    >
      {/* Substitution and always-served are marked visually — a glyph in the
          line and a border treatment. Neither has a spoken form, so a
          substituted item read identically to a normal one. These prefixes
          are the text equivalent of the marks. */}
      {item.isSubstitution && <span className="sr-only">Substitution: </span>}
      {item.isAlwaysServed && <span className="sr-only">Always served: </span>}
      <span className="ttvc-item-text" style={itemColorStyle}>
        {line}
      </span>
      {item.isSubstitution && item.substitutionReason && (
        <span className="ttvc-item-consist"> [{item.substitutionReason}]</span>
      )}
    </div>
  );
}

/**
 * Composes the spoken read-out of a ticket, from the ticket's own data.
 *
 * Deliberately not scraped from the rendered DOM: the layout is the thing that
 * was unreadable, so reading it back would reproduce exactly the run-together
 * blob this whole effort removed, and would break the moment the visual
 * arrangement changed.
 *
 * Order follows what matters clinically rather than what the ticket puts where
 * — who it is for, what they may not have, then what is on the tray. Allergies
 * come before food on purpose.
 */
function summariseTicket(patient: TrayTicketPatient): ScreenSummaryValue {
  const items = patient.items ?? [];
  const diet = patient.dietTypes?.join(', ') || 'none recorded';
  const consist = patient.consistencies?.join(', ') || 'none recorded';
  const allergies = patient.allergies?.length
    ? patient.allergies.join(', ')
    : 'none recorded';

  const summary = `Tray ticket for ${patient.fullName}. `
    + `Diet ${diet}. Consistency ${consist}. Allergies ${allergies}. `
    + `${items.length} ${items.length === 1 ? 'item' : 'items'} on the tray.`;

  const sections: ScreenSummarySection[] = [];

  if (items.length) {
    sections.push({
      label: 'Items',
      lines: items.map((it, i) => {
        // Each line stands alone: she may hear it out of context, and "2
        // slices" means nothing without the food it belongs to.
        const bits = [`${i + 1}. ${it.itemName}`];
        if (it.portion) bits.push(it.portion);
        if (it.consistency) bits.push(it.consistency);
        if (it.modifier) bits.push(it.modifier);
        if (it.serviceItem) bits.push(`served on ${it.serviceItem}`);
        if (it.isSubstitution) bits.push('substitution');
        if (it.isAlwaysServed) bits.push('always served');
        return bits.join(', ');
      }),
    });
  }

  if (patient.specialDevices) {
    sections.push({ label: 'Special devices', lines: [patient.specialDevices] });
  }

  return { summary, sections };
}

export default function TrayTicketVisualizerContent({ patient }: TrayTicketVisualizerContentProps) {
  const colorConfig = useTrayTicketColorConfig();
  const useColor = colorConfig?.useColor ?? false;
  // Facility toggle — WinForms `TT_UseCapsOnItems` uppercases the
  // whole item line (`RenderMenu` .vb:2496-2498).
  const facilitySettings = useGlobalStore((s) => s.facilitySettings);
  const useCaps = !!(facilitySettings as Record<string, unknown> | null)?.tT_UseCapsOnItems;

  // Each item carries its own per-side border bitmask — no
  // grouping needed. Consecutive items whose sides align form
  // a visual box automatically.
  // One key reads the whole ticket rather than making her tab it item by item.
  // Null while the ticket has no resident: a confident summary of an empty
  // ticket is worse than none.
  useScreenSummary(patient?.fullName ? summariseTicket(patient) : null);

  const items = patient.items || [];
  const hasItems = items.length > 0;

  // Allergy styling now lives in the parent dialog's banner —
  // `allergyStyle` was pruned along with the allergies row here.

  // Likes/Dislikes styles
  const dislikesStyle: React.CSSProperties = {};
  const likesStyle: React.CSSProperties = {};
  if (useColor && colorConfig) {
    if (colorConfig.dislikes && colorConfig.dislikes !== '#000000') dislikesStyle.color = colorConfig.dislikes;
    if (colorConfig.likes && colorConfig.likes !== '#000000') likesStyle.color = colorConfig.likes;
  }

  // Notes style
  const notesStyle: React.CSSProperties = {};
  if (useColor && colorConfig) {
    if (colorConfig.notes && colorConfig.notes !== '#000000') notesStyle.color = colorConfig.notes;
  }

  return (
    <div className="ttvc-container">
      {/* NPO / Discharged / Deleted overlays */}
      {patient.isNPO && (
        <div className="ttvc-overlay-npo">NPO</div>
      )}
      {patient.isDischarged && (
        <div className="ttvc-overlay-discharged">{patient.fullName} Is Discharged</div>
      )}
      {patient.isDeleted && (
        <div className="ttvc-overlay-deleted">{patient.fullName} Is Deleted</div>
      )}

      {/* Mealtime banner + diet types / consistencies / allergies
          are all rendered by the parent `TrayTicketVisualizerDialog`
          as full-width bands ABOVE this tray body (matches the
          WinForms layout), so we skip them here to avoid duplication. */}

      {/* Feeding status / special devices are rendered by the
          parent `TrayTicketVisualizerDialog` as their own band
          between the diet-order banner and the mealtime banner,
          so we skip them here. Custom message + tube-feed indicator
          are also handled in the parent's special-messages column
          (built by `Diet_Utils.TrayTicketGetMessageText`). */}

      {/* Menu items */}
      {/* The ticket conveys itself through layout: each line is a plain div,
          so a screen reader ran the whole body together as one sentence —
          "Regular 1 serving Scrambled Eggs Hot Plate Regular 2 slices Whole
          Wheat Toast...". List semantics give the boundaries the columns give
          a sighted reader, and the count up front ("list, 3 items") answers
          "how much food is on this tray" without reading it all. */}
      <div className="ttvc-items" role="list" aria-label="Tray items">
        {!hasItems ? (
          <div className="ttvc-no-items">No menu items for this mealtime.</div>
        ) : (
          items.map((item, i) => (
            <ItemRow key={i} item={item} colorConfig={colorConfig} useCaps={useCaps} />
          ))
        )}
      </div>

      {/* Dislikes */}
      {patient.dislikes && (
        <div className="ttvc-special" style={dislikesStyle}>
          <span className="ttvc-special-label">DISLIKES: </span>
          {patient.dislikes}
        </div>
      )}

      {/* Likes */}
      {patient.likes && (
        <div className="ttvc-special" style={likesStyle}>
          <span className="ttvc-special-label">LIKES: </span>
          {patient.likes}
        </div>
      )}

      {/* Special instructions */}
      {patient.specialInstructions && (
        <div className="ttvc-special" style={notesStyle}>
          <span className="ttvc-special-label">NOTES: </span>
          {patient.specialInstructions}
        </div>
      )}
    </div>
  );
}
